import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getUserSurvey, saveSurveyDraft, saveSubmittedSurvey, submitSurvey } from "../../api/api";
import SignedOutPrompt from "../SignedOutPrompt";
import SurveyRunner from "../survey/SurveyRunner";

/**
 * Take, continue, or edit a survey.
 *
 * @param {Object} props
 * @param {number} [props.userId]
 * @param {object} [props.user]
 * @returns {JSX.Element}
 */
export default function SurveyTakePage({ userId, user }) {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!userId || !surveyId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      const result = await getUserSurvey(userId, surveyId);
      if (!mounted) return;
      if (result.error) {
        setError(result.error);
        setDetail(null);
      } else {
        setDetail(result.data);
      }
      setLoading(false);
    }
    load();
    return () => {
      mounted = false;
    };
  }, [userId, surveyId]);

  if (!userId) {
    return (
      <SignedOutPrompt
        title="Beta feedback"
        description="Sign in to complete this survey."
      />
    );
  }

  if (user?.type === "organization") {
    return (
      <SignedOutPrompt
        variant="restricted"
        title="Beta feedback"
        description="Beta surveys are available on personal accounts."
      />
    );
  }

  const definition = detail?.definition;
  const draft = detail?.draft;
  const submitted = detail?.submitted;
  const allowEdit = Boolean(definition?.allowEdit);
  const allowRetake = Boolean(definition?.allowRetake);
  const locked = Boolean(definition && !allowRetake && submitted && !allowEdit && !draft);

  let mode = "draft";
  let initialAnswers = {};
  let initialPageId = "";
  if (draft) {
    mode = "draft";
    initialAnswers = draft.answers || {};
    initialPageId = draft.current_page_id || "";
  } else if (!allowRetake && submitted && allowEdit) {
    mode = "edit";
    initialAnswers = submitted.answers || {};
    initialPageId = submitted.current_page_id || "";
  }

  /**
   * @param {Record<string, unknown>} answers
   * @param {string} pageId
   * @returns {Promise<Object>}
   */
  async function handleSave(answers, pageId) {
    if (mode === "edit") {
      return saveSubmittedSurvey(userId, surveyId, answers, pageId);
    }
    return saveSurveyDraft(userId, surveyId, answers, pageId);
  }

  /**
   * @param {Record<string, unknown>} answers
   * @param {string} pageId
   * @returns {Promise<Object>}
   */
  async function handleSubmit(answers, pageId) {
    return submitSurvey(userId, surveyId, answers, pageId);
  }

  return (
    <div className="bg-ui-background min-h-[calc(100dvh-3.5rem)] w-full">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:py-10">
        <Link to={`/beta/${surveyId}`} className="text-sm text-accent hover:text-accent-hover">
          ← {definition?.title || "Survey"}
        </Link>
        {loading ? <p className="mt-8 text-sm text-text-muted">Loading survey…</p> : null}
        {error ? (
          <p className="mt-6 text-sm text-error" role="alert">
            {error}
          </p>
        ) : null}
        {locked ? (
          <p className="mt-8 text-sm text-text-secondary">
            This survey is complete and cannot be edited.{" "}
            <Link to={`/beta/${surveyId}`} className="text-accent hover:text-accent-hover">
              Back to intro
            </Link>
          </p>
        ) : null}
        {definition && !locked ? (
          <div className="mt-6">
            <SurveyRunner
              key={`${surveyId}-${mode}-${draft?.id || submitted?.id || "new"}`}
              definition={definition}
              initialAnswers={initialAnswers}
              initialPageId={initialPageId}
              mode={mode}
              onSave={handleSave}
              onSubmit={handleSubmit}
              onDone={() => navigate(`/beta/${surveyId}/thanks`, { replace: true })}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
